import { useState } from "react";
import { languages } from "../../constant/Languages";
import "./inputStyle.css";
import { MailDataType, NewTemplateObj } from "./mailTemplate";
import { OtherMailDataType } from "./OtherEmail";

type MailInputComponentProps = {
  label: string;
  placeholder: string;
  variables?: string;
  name: string;
  templateData: NewTemplateObj[];
  mailData: MailDataType | OtherMailDataType;
  onEmailTemplateChange: (
    mailData: MailDataType | OtherMailDataType,
    elm: NewTemplateObj,
    type: string
  ) => void;
};

const MailInputComponent: React.FC<MailInputComponentProps> = ({
  label,
  mailData,
  placeholder,
  variables,
  name,
  templateData,
  onEmailTemplateChange,
}) => {
  const [showInputField, setShowInputField] = useState(false);
  const [templateState, setTemplateState] = useState<NewTemplateObj>({
    language: "",
    htmlTemplate: "",
    error: "",
    isRendering: false,
    subject: "",
    id: Math.floor(Math.random() * 3000) + 1, // Unique ID for new templates
  });

  // Validate HTML content
  const validateHTML = (html: string): string | false => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.querySelector("parsererror") ? false : html;
  };

  // Save a new template
  const handleSave = () => {
    if (!templateState.language || !templateState.htmlTemplate) {
      setTemplateState((prev) => ({
        ...prev,
        error: "Language and HTML template are required.",
      }));
      return;
    }

    const validatedHTML = validateHTML(templateState.htmlTemplate);
    if (!validatedHTML) {
      setTemplateState((prev) => ({
        ...prev,
        error: "Invalid HTML content. Please correct it and try again.",
      }));
      return;
    }
    console.log("inside save", templateState);
    onEmailTemplateChange(
      mailData,
      { ...templateState, htmlTemplate: validatedHTML },
      "add"
    );

    setTemplateState({
      language: "",
      htmlTemplate: "",
      error: "",
      isRendering: false,
      subject: "",
      id: Math.floor(Math.random() * 3000) + 1,
    });

    setShowInputField(false);
  };

  const toggleRenderView = (index: number) => {
    const updatedTemplate = {
      ...templateData[index],
      isRendering: !templateData[index].isRendering,
    };
    onEmailTemplateChange(mailData, updatedTemplate, "update");
  };

  const handleInputChange = (
    field: keyof NewTemplateObj,
    value: string,
    index: number
  ) => {
    if (!mailData.isEditMode) return;
    console.log("input cahnge", field, value, index);
    const updatedTemplate = { ...templateData[index], [field]: value };
    console.log("update temp", updatedTemplate);
    onEmailTemplateChange(mailData, updatedTemplate, "update");
  };

  const handleRemove = (index: number) => {
    const templateToRemove = templateData[index];
    onEmailTemplateChange(mailData, templateToRemove, "remove");
  };

  return (
    <div className="mt-2">
      {/* Section Header */}
      <div className="flex items-center gap-2 mb-3">
        <svg className="w-5 h-5 text-[#6ECDC5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
        <h3 className="text-base font-semibold text-[#1a1a2e] font-roboto">
          {label}
        </h3>
      </div>

      {variables && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-3">
          <p className="text-xs text-blue-600 font-medium">{variables}</p>
        </div>
      )}

      <div className="space-y-4">
        {/* Existing Templates */}
        {templateData?.map((item, index) => (
          <div
            key={item.language + index}
            className="border border-gray-200 rounded-xl bg-white overflow-hidden"
          >
            {/* Template Header */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex flex-wrap items-center gap-4">
                {/* Language Select */}
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">Language</label>
                  <select
                    value={item.language}
                    onChange={(e) =>
                      handleInputChange("language", e.target.value, index)
                    }
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white
                      focus:ring-2 focus:ring-[#6ECDC5] focus:border-[#6ECDC5] outline-none"
                  >
                    <option value="">Select Language</option>
                    {languages.map((lang) => (
                      <option key={lang.code} value={lang.name}>
                        {lang.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Subject Input */}
                <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                  <label className="text-xs font-medium text-[#6B7280] uppercase tracking-wide whitespace-nowrap">Subject</label>
                  <input
                    type="text"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white
                      focus:ring-2 focus:ring-[#6ECDC5] focus:border-[#6ECDC5] outline-none"
                    value={item.subject}
                    onChange={(e) =>
                      handleInputChange("subject", e.target.value, index)
                    }
                    placeholder="Enter subject"
                  />
                </div>
              </div>
            </div>

            {/* Template Body */}
            <div className="p-4">
              <label className="block text-xs font-medium text-[#6B7280] uppercase tracking-wide mb-2">
                HTML Template
              </label>
              {item.isRendering ? (
                <div className="w-full h-[200px] overflow-hidden bg-white border border-gray-200 rounded-lg">
                  <iframe
                    srcDoc={`
                      <!DOCTYPE html>
                      <html>
                        <head>
                          <style>
                            body {
                              margin: 0;
                              padding: 8px;
                              font-family: Arial, sans-serif;
                              background-color: #f9f9f9;
                            }
                          </style>
                        </head>
                        <body>${item.htmlTemplate}</body>
                      </html>
                    `}
                    className="w-full h-full border-0"
                    sandbox="allow-same-origin"
                  />
                </div>
              ) : (
                <textarea
                  className="w-full h-[200px] px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-mono
                    bg-[#fafafa] text-gray-800 focus:ring-2 focus:ring-[#6ECDC5] focus:border-[#6ECDC5] outline-none resize-y"
                  value={item.htmlTemplate}
                  onChange={(e) =>
                    handleInputChange("htmlTemplate", e.target.value, index)
                  }
                  placeholder="Enter HTML content here..."
                />
              )}
              {item.error && (
                <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                  {item.error}
                </p>
              )}

              {/* Template Actions */}
              <div className="flex gap-2 mt-3">
                <button
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#6ECDC5] text-white rounded-lg text-sm
                    font-medium hover:bg-[#5bb8b0] transition-colors duration-200"
                  type="button"
                  onClick={() => toggleRenderView(index)}
                >
                  {item.isRendering ? (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>
                      View HTML
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      Preview
                    </>
                  )}
                </button>
                {mailData.isEditMode && (
                  <button
                    onClick={() => handleRemove(index)}
                    type="button"
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-500 border border-red-200
                      rounded-lg text-sm font-medium hover:bg-red-500 hover:text-white transition-colors duration-200"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* New Template Form */}
        {showInputField && (
          <div className="border-2 border-dashed border-[#6ECDC5] rounded-xl bg-[#E8F8F5]/30 p-5">
            <h4 className="text-sm font-semibold text-[#1a1a2e] mb-4">New Template</h4>

            <div className="space-y-4">
              {/* Language Select */}
              <div>
                <label className="block text-xs font-medium text-[#6B7280] uppercase tracking-wide mb-1.5">Language</label>
                <select
                  value={templateState.language}
                  onChange={(e) =>
                    setTemplateState((prev) => ({
                      ...prev,
                      language: e.target.value,
                    }))
                  }
                  className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white
                    focus:ring-2 focus:ring-[#6ECDC5] focus:border-[#6ECDC5] outline-none"
                >
                  <option value="">Select Language</option>
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.name}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-xs font-medium text-[#6B7280] uppercase tracking-wide mb-1.5">Subject</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white
                    focus:ring-2 focus:ring-[#6ECDC5] focus:border-[#6ECDC5] outline-none"
                  value={templateState.subject}
                  onChange={(e) =>
                    setTemplateState((prev) => ({
                      ...prev,
                      subject: e.target.value,
                    }))
                  }
                  placeholder="Enter subject"
                />
              </div>

              {/* HTML Content */}
              <div>
                <label className="block text-xs font-medium text-[#6B7280] uppercase tracking-wide mb-1.5">HTML Template</label>
                <textarea
                  className="w-full h-[200px] px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-mono
                    bg-white text-gray-800 focus:ring-2 focus:ring-[#6ECDC5] focus:border-[#6ECDC5] outline-none resize-y"
                  value={templateState.htmlTemplate}
                  onChange={(e) =>
                    setTemplateState((prev) => ({
                      ...prev,
                      htmlTemplate: e.target.value,
                    }))
                  }
                  placeholder="Enter HTML content ..."
                />
              </div>

              {templateState.error && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                  {templateState.error}
                </p>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleSave}
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#6ECDC5] text-white rounded-lg text-sm
                    font-medium hover:bg-[#5bb8b0] transition-colors duration-200 shadow-sm"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Save Template
                </button>
                <button
                  onClick={() => setShowInputField(false)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-[#6B7280] rounded-lg text-sm
                    font-medium hover:bg-gray-200 transition-colors duration-200"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Template Button */}
        {mailData.isEditMode && (
          <button
            type="button"
            className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 text-[#6B7280]
              rounded-lg text-sm font-medium hover:border-[#6ECDC5] hover:text-[#6ECDC5] transition-colors duration-200 w-full justify-center"
            onClick={() => setShowInputField(true)}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Add More Template
          </button>
        )}
      </div>
    </div>
  );
};

export default MailInputComponent;
